import * as Color from "color"
import PptxGenJS, {
	ChartBarDataset,
	ChartOptions ,
	HTMLToPowerPointOptions,
	ChartPieDataset,
	PlaceholderOptions,
	ChartData
} from "pptxgenjs"
import * as React from "react"
import * as ReactDOM from "react-dom"
import { IntlProvider } from "react-intl"
import DebtTable, { Decomposition } from "./debt-table"
import { getChartBaseOptions, getChartSecondOfTwoOptions, getChartFirstOfTwoOptions, getChartColors } from "../pptx-configuration/charts"
import { MasterSlide } from "../pptx-configuration/master-slides"
import {
	getPlaceholderChartBodyOptions,
	Placeholder
} from "../pptx-configuration/placeholders"
import * as Conf from "../../configuration"
import * as CommonUtils from "../utils"
import * as BankDomain from "../../../../domain/sfp/bank-domain"
import * as LoanDomain from "../../../../domain/sfp/loan-domain"
import * as Model from "../../../../model"
import { ColorsHelper } from "../../../../util/colors"

export function createGlobalChartOptions(baseColor: Color): ChartOptions {
	return {
		...getChartBaseOptions(getPlaceholderChartBodyOptions()),
		barGrouping: "stacked",
		chartColors: getChartColors(),
		legendFontSize : 11,
		legendPos: "b",
		showTitle: false
	}
}

export function createChartData(
	decomposition: Decomposition,
	decomposedLoans: Model.LoanWithDecomposition[],
	years: number[]
): [ChartBarDataset, ChartBarDataset] {
	return decomposedLoans.reduce<[ChartBarDataset, ChartBarDataset]>(
		([realDataset, simulatedDataset], decomposedLoan) => {
			years.forEach((year, yearIndex) => {
				const value = getValue(decomposition, decomposedLoan.decomposition, year)

				if (decomposedLoan.loan.simulate)
					simulatedDataset.values[yearIndex] += value
				else
					realDataset.values[yearIndex] += value
			})
			return [realDataset, simulatedDataset]
		}, [initDataset(false, years), initDataset(true, years)]
	)
}

export function createPerBankChartData(
	decomposedLoans: Model.LoanWithDecomposition[],
	decomposition: "annuity" | "interest" | "remaining" | "reimbursed",
	year: number
): [ChartPieDataset] {
	const banks = LoanDomain.distinctBanks(decomposedLoans.map(d => d.loan))

	return [banks.reduce<ChartPieDataset>((dataset, bank, bankIndex) => {
		dataset.labels.push(bank ? bank.label : "NC")
		dataset.values.push(0)

		for (const decomposedLoan of decomposedLoans) {
			if (decomposedLoan.loan.simulate) continue
			if (!BankDomain.equal(bank, decomposedLoan.loan.bank)) continue

			const value =
			decomposition === "annuity" ? LoanDomain.getAnnuity(
				decomposedLoan.decomposition,
				year
			) :
			decomposition === "interest" ? LoanDomain.getYearInterest(
				decomposedLoan.decomposition,
				year
			) :
			decomposition === "reimbursed" ? LoanDomain.getYearReimboursedCapital(
				decomposedLoan.decomposition,
				year
			) :
			decomposition === "remaining" ? LoanDomain.getYearRemainingCapital(
				decomposedLoan.decomposition,
				year
			) : 0

			dataset.values[bankIndex] += value
		}

		return dataset
	}, { labels: [], name: "Par banque", values: [] })]
}

export function createPerBankChartOptions(
	data: [ChartPieDataset],
	coupled: boolean,
	placeholderBodyOptions: PlaceholderOptions,
	baseColor: Color
): ChartOptions {
	const options: ChartOptions = coupled
	? { ...getChartFirstOfTwoOptions(placeholderBodyOptions) }
	: { ...getChartBaseOptions(placeholderBodyOptions) }
	options.title = "Par banque"
	options.chartColors = getChartColors();
	options.legendFontSize = 11;
	return options
}

export function createPerRateTypeChartData(
	decomposedLoans: Model.LoanWithDecomposition[],
	decomposition: "annuity" | "interest" | "remaining" | "reimbursed",
	year: number
): [ChartPieDataset] {
	const labels = ["Taux fixe", "Taux variable"]
	return [decomposedLoans.reduce<ChartPieDataset>((dataset, decomposedLoan) => {
		const value =
		decomposition === "annuity" ? LoanDomain.getAnnuity(
			decomposedLoan.decomposition,
			year
		) :
		decomposition === "interest" ? LoanDomain.getYearInterest(
			decomposedLoan.decomposition,
			year
		) :
		decomposition === "reimbursed" ? LoanDomain.getYearReimboursedCapital(
			decomposedLoan.decomposition,
			year
		) :
		decomposition === "remaining" ? LoanDomain.getYearRemainingCapital(
			decomposedLoan.decomposition,
			year
		) : 0

		if (decomposedLoan.loan.variableRate) dataset.values[1] += value
		else dataset.values[0] += value

		return dataset
	}, { labels, name: "Par taux", values: [0, 0] })]
}

export function createPerRateTypeChartOptions(
	coupled: boolean,
	placeholderBodyOptions: PlaceholderOptions,
	baseColor: Color
): ChartOptions {
	const options = coupled
	? { ...getChartSecondOfTwoOptions(placeholderBodyOptions) }
	: { ...getChartBaseOptions(placeholderBodyOptions) }
		options.title = "Par taux"
		options.chartColors = getChartColors()
		options.legendFontSize = 11;
	return options
}

export function createYears(
	conf: Conf.ReportConfiguration,
	decomposedLoans: Model.LoanWithDecomposition[]
): number[] {
	const currentYear = new Date().getFullYear()
	const decompositions = decomposedLoans.map(lwd => lwd.decomposition)
	const start = conf.generalities.years[0] ?conf.generalities.years[0] : currentYear
	const end = LoanDomain.findLastPaymentDate(decompositions) && LoanDomain.findLastPaymentDate(decompositions).getFullYear() ?
	LoanDomain.findLastPaymentDate(decompositions).getFullYear()
	: currentYear - 1
	return CommonUtils.createYears(start, end)
}

export function includeDebtTable(
	pptx: PptxGenJS,
	slidesTitle: string,
	tableBaseId: string,
	decomposition: Decomposition,
	childDecomposedLoans: any,
	years: number[],
	conf: Conf.ReportConfiguration,
	checked:string
): void {
	const maxColumns = 10

	const tablesCount = years.length > maxColumns ?
	Math.round(years.length / maxColumns) :1

	let check = checked
	for (let i = 0; i < tablesCount; i++) {
		const tempDiv = document.createElement("div")
		document.head.appendChild(tempDiv)

		const tableId = `${tableBaseId}-${i}`
		const subYearStart = i * maxColumns
		const subYears = years.slice(subYearStart, subYearStart + maxColumns)
		const table = <IntlProvider locale={navigator.language} defaultLocale="fr">
			<DebtTable
			currency={conf.currency}
			decomposition={decomposition}
			id={tableId}
			locale={conf.locale}
			childDecomposedLoans={childDecomposedLoans}
			roundness={conf.generalities.roundness}
			tableConfiguration={conf.table}
			years={subYears}
			checked={check} />
		</IntlProvider>

		ReactDOM.render(table, tempDiv, () => {
			const slidesOptions: HTMLToPowerPointOptions = {
				addText: {
					text: slidesTitle,
					opts: { placeholder: Placeholder.Title }
				},
				addHeaderToEach: true,
				master: MasterSlide.Table
			}
			pptx.addSlidesForTable(tableId, slidesOptions)
			document.head.removeChild(tempDiv)
		})
	}
}

function initDataset(
	simulated: boolean,
	years: number[]
): ChartBarDataset {
	const labels = years.map(y => y.toString())
	const name = simulated ? "Emprunts simulés" : "Emprunts contractés"
	const values = years.map(() => 0)
	return { labels, name, values }
}

function getValue(
	decomposition: Decomposition,
	loanDecomposition: Model.LoanDecomposition,
	year: number
): number {
	switch (decomposition) {
		case Decomposition.Annuity:
			return LoanDomain.getAnnuity(loanDecomposition, year)
		case Decomposition.CapitalReimbursement:
			return LoanDomain.getYearReimboursedCapital(loanDecomposition, year)
		case Decomposition.InterestReimbursement:
			return LoanDomain.getYearInterest(loanDecomposition, year)
		case Decomposition.RemainingCapital:
			return LoanDomain.getYearRemainingCapital(loanDecomposition, year)
	}
}

export function formatCurrency(
	locale: string,
	currency: string,
	value: number,
	roundness?: Conf.RoundnessValue,
): string {
	switch (roundness) {
		case "nearest-thousand":
		case "nearest-whole-number":
			let formatter = new Intl.NumberFormat(locale, {
				currency,
				maximumFractionDigits: 0,
				minimumFractionDigits: 0,
				style: "currency"
			})
			switch (roundness) {
				case "nearest-thousand":
					return formatter.format(value / 1000).replace(/^(.*\s)(.)$/, "$1k$2")
				case "nearest-whole-number":
					return formatter.format(value)
			}
		default:
			formatter = new Intl.NumberFormat(locale, { currency, style: "currency" })
			return formatter.format(value)
	}
}

export function formatDate(date: Date): string {
	const day = date.getDate()
	const month = date.getMonth()
	const year = date.getFullYear()
	return `${day}/${month}/${year}`
}

export function formatTitle(title: string, numbers: number[]): string {
	return `${numbers.join(".")}. ${title}`
}

export function toInches(cm: number): number {
	return Math.round((cm / 2.54) * 10) / 10
}



// WEBPACK FOOTER //
// ./src/component/management-report/generator/budgetGenerator/utils.tsx